import React, { useEffect, useState } from "react";
import { Button } from "../common";
import {
  useAppDispatch,
  useAppSelector,
  searchAnnouncementsThunk,
  createAnnouncementThunk,
  editAnnouncementThunk,
} from "../../redux";
import { showSuccess } from "../../services";
import { AnnouncementRecord } from "../../types";
import "./AnnouncementModal.css";

interface AnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AnnouncementFormState {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
}

const EMPTY_FORM: AnnouncementFormState = {
  name: "",
  description: "",
  startDate: "",
  endDate: "",
};

const buildTeacherAnnouncementsQuery = (teacherId: string) =>
  `select id, Name, Description, Start_Date, End_Date, Teacher.Name from Announcements where Teacher = '${teacherId}' order by Start_Date desc limit 200`;

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const records = useAppSelector((state) => state.announcement.records);
  const isLoading = useAppSelector((state) => state.announcement.isLoading);

  const [activeTab, setActiveTab] = useState<"create" | "manage">("create");
  const [form, setForm] = useState<AnnouncementFormState>(EMPTY_FORM);
  const [editingItem, setEditingItem] = useState<AnnouncementRecord | null>(
    null,
  );

  useEffect(() => {
    if (isOpen && user?.id) {
      dispatch(
        searchAnnouncementsThunk({
          selectQuery: buildTeacherAnnouncementsQuery(user.id),
        }),
      );
    }
  }, [isOpen, user?.id, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab("create");
      setForm(EMPTY_FORM);
      setEditingItem(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const refreshList = () => {
    if (user?.id) {
      dispatch(
        searchAnnouncementsThunk({
          selectQuery: buildTeacherAnnouncementsQuery(user.id),
          force: true,
        }),
      );
    }
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !form.name.trim()) return;

    dispatch(
      createAnnouncementThunk({
        module: "Announcements",
        fields: {
          Name: form.name,
          Description: form.description,
          Start_Date: form.startDate || undefined,
          End_Date: form.endDate || undefined,
          Teacher: { id: user.id },
        },
      }),
    ).then((res) => {
      if (createAnnouncementThunk.fulfilled.match(res)) {
        showSuccess("Announcement created successfully!");
        setForm(EMPTY_FORM);
        refreshList();
        setActiveTab("manage");
      }
    });
  };

  const handleOpenEdit = (item: AnnouncementRecord) => {
    setEditingItem(item);
    setForm({
      name: item.Name || "",
      description: item.Description || "",
      startDate: item.Start_Date || "",
      endDate: item.End_Date || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !form.name.trim()) return;

    dispatch(
      editAnnouncementThunk({
        module: "Announcements",
        recordId: editingItem.id,
        fields: {
          Name: form.name,
          Description: form.description,
          Start_Date: form.startDate || undefined,
          End_Date: form.endDate || undefined,
        },
      }),
    ).then((res) => {
      if (editAnnouncementThunk.fulfilled.match(res)) {
        showSuccess("Announcement updated successfully!");
        handleCancelEdit();
        refreshList();
      }
    });
  };

  const showForm = activeTab === "create" || Boolean(editingItem);

  return (
    <div
      className="announcement-modal-overlay"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-labelledby="announcement-modal-title"
    >
      <div
        className="announcement-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="announcement-modal-header">
          <h3
            id="announcement-modal-title"
            className="announcement-modal-title"
          >
            Announcements
          </h3>
          <button
            type="button"
            className="announcement-modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <div className="announcement-modal-body">
          <div className="announcement-tabs">
            <button
              type="button"
              className={`announcement-tab-btn ${activeTab === "create" ? "active" : ""}`}
              onClick={() => {
                handleCancelEdit();
                setActiveTab("create");
              }}
            >
              Create Announcement
            </button>
            <button
              type="button"
              className={`announcement-tab-btn ${activeTab === "manage" ? "active" : ""}`}
              onClick={() => setActiveTab("manage")}
            >
              Manage Announcements
            </button>
          </div>

          {showForm && (
            <form
              onSubmit={editingItem ? handleEditSubmit : handleCreateSubmit}
              className="announcement-form-grid"
            >
              <div className="announcement-field full-width">
                <label>Title</label>
                <input
                  type="text"
                  placeholder="Enter announcement title"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="announcement-field">
                <label>Start Date</label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                />
              </div>

              <div className="announcement-field">
                <label>End Date</label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                />
              </div>

              <div className="announcement-field full-width">
                <label>Description</label>
                <textarea
                  placeholder="Enter announcement description"
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <div
                className="announcement-field full-width"
                style={{ flexDirection: "row", gap: "12px" }}
              >
                <Button
                  type="submit"
                  variant="primary"
                  size="medium"
                  disabled={isLoading}
                >
                  {isLoading
                    ? "Saving..."
                    : editingItem
                      ? "Save Changes"
                      : "Create Announcement"}
                </Button>
                {editingItem && (
                  <Button
                    type="button"
                    variant="outline"
                    size="medium"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          )}

          {activeTab === "manage" && !editingItem && (
            <div className="announcement-list">
              {records.length === 0 && (
                <div className="announcement-empty-state">
                  {isLoading
                    ? "Loading announcements..."
                    : "No announcements created yet."}
                </div>
              )}
              {records.map((item) => (
                <div className="announcement-list-item" key={item.id}>
                  <div className="announcement-list-item-header">
                    <span className="announcement-list-item-title">
                      {item.Name}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="small"
                      onClick={() => handleOpenEdit(item)}
                    >
                      Edit
                    </Button>
                  </div>
                  {(item.Start_Date || item.End_Date) && (
                    <span className="announcement-list-item-dates">
                      {item.Start_Date || "—"} to {item.End_Date || "—"}
                    </span>
                  )}
                  {item.Description && (
                    <span className="announcement-list-item-desc">
                      {item.Description}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;
